/**
 * Idioma de la interfaz (plan de idioma, F0, 7-oct-2026; `PLAN_IDIOMA_INGLES_202610.md`).
 *
 * La clave de cada texto es el propio texto en español: `t('Guardar cliente')`.
 *  · En español no hay catálogo: sale la clave, con sus variables ya puestas.
 *  · En inglés se busca en `locales/en.json`, que se carga solo si hace falta.
 *  · Lo que no tenga traducción sale en español, nunca en blanco.
 * Plurales en inglés: `"Faltan {{count}} documentos_one"` / `"…_other"` en el catálogo.
 *
 * El país decide QUÉ se pide y qué ley se cita; el idioma, CÓMO se dice. Lo elige cada
 * usuario arriba (`SelectorIdioma`) entre los que su despacho tiene habilitados; llega del
 * servidor en `user.idioma` y se recuerda en el navegador para que la entrada y la carga no
 * salgan un instante en el otro idioma. Lo que va al supervisor (F19, RTE, UAF) sigue en
 * español.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export type Idioma = 'es' | 'en';
export const IDIOMAS: Idioma[] = ['es', 'en'];
export const IDIOMA_BASE: Idioma = 'es';
/** Cada idioma, en su propia lengua: no se traduce. */
export const NOMBRE_IDIOMA: Record<Idioma, string> = { es: 'Español', en: 'English' };

const CLAVE_LOCAL = 'idioma';

export const esIdioma = (v: unknown): v is Idioma => v === 'es' || v === 'en';

/** El último idioma usado en este navegador (o español). Nunca lanza. */
export const idiomaRecordado = (): Idioma => {
  try {
    const v = localStorage.getItem(CLAVE_LOCAL);
    return esIdioma(v) ? v : IDIOMA_BASE;
  } catch {
    return IDIOMA_BASE;
  }
};

export const recordar = (idioma: Idioma): void => {
  try {
    localStorage.setItem(CLAVE_LOCAL, idioma);
  } catch {
    /* sin almacenamiento: se usará el del servidor en la próxima carga */
  }
};

// Lo que ve el cliente final (KYC público, subsanación, documentos, Formulario A) no sigue el
// idioma del usuario del despacho: un usuario que trabaja en inglés y abre uno de esos enlaces en
// su navegador lo ve igual que el cliente. Empieza en español y solo cambia si el cliente elige el
// inglés en su formulario (abajo, «El idioma del cliente»).
const PAGINAS_DEL_CLIENTE = /^\/(kyc|kyc-docs|kyc-subsanacion|formulario-a)\//;
export const esPaginaDelCliente = (ruta?: string): boolean => {
  try {
    return PAGINAS_DEL_CLIENTE.test(ruta ?? window.location.pathname);
  } catch {
    return false;
  }
};

// ── El idioma del cliente (plan de idioma, el cliente final en inglés, fase C1, 3-nov-2026) ─────
// Lo elige el cliente con «ES | EN» arriba (decisión a): empieza en español y se recuerda para ESE
// enlace, con su propia clave, para no pisar el idioma del usuario del despacho en este navegador.
// Cambiarlo recarga la página, como en el portal: así salen también los textos que se traducen al
// cargar un módulo (casillas, documentos, el árbol). El borrador, el paso y el código de acceso se
// guardan en el navegador y sobreviven a la recarga. El Formulario A (agente residente) no cambia.
const ENLACE_DEL_CLIENTE = /^\/(kyc|kyc-docs|kyc-subsanacion)\/([^/?#]+)/;
const CLAVE_IDIOMA_PUBLICO = 'idioma_formulario_publico:';
let recargandoPorIdioma = false;

const tokenDelEnlace = (ruta?: string): string | null => {
  try {
    return (ruta ?? window.location.pathname).match(ENLACE_DEL_CLIENTE)?.[2] ?? null;
  } catch {
    return null;
  }
};

/** El idioma que el cliente eligió para este enlace (o español). Nunca lanza. */
export const idiomaPublicoRecordado = (ruta?: string): Idioma => {
  const token = tokenDelEnlace(ruta);
  if (!token) return IDIOMA_BASE;
  try {
    const v = localStorage.getItem(CLAVE_IDIOMA_PUBLICO + token);
    return esIdioma(v) ? v : IDIOMA_BASE;
  } catch {
    return IDIOMA_BASE;
  }
};

/** Para el aviso «al salir» de los formularios: la recarga del cambio de idioma no pierde nada. */
export const recargaPorIdioma = (): boolean => recargandoPorIdioma;

/** El cliente cambia de idioma: se recuerda para este enlace y se recarga la página. */
export const cambiarIdiomaPublico = (idioma: Idioma): void => {
  const token = tokenDelEnlace();
  if (!token) return;
  try {
    if (idioma === IDIOMA_BASE) localStorage.removeItem(CLAVE_IDIOMA_PUBLICO + token);
    else localStorage.setItem(CLAVE_IDIOMA_PUBLICO + token, idioma);
  } catch {
    return; // sin almacenamiento, la recarga volvería a español: mejor no cambiar
  }
  recargandoPorIdioma = true;
  window.location.reload();
};

i18n.use(initReactI18next).init({
  // El idioma se fija ya, sin esperar al catálogo: así las fechas y los números calculados
  // al cargar (localeUI) salen en el idioma bueno. Los textos llegan con `prepararIdioma`.
  lng: esPaginaDelCliente() ? IDIOMA_BASE : idiomaRecordado(),
  fallbackLng: IDIOMA_BASE,
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  resources: { es: { translation: {} } },
  react: { useSuspense: false },
});

const cargados = new Set<Idioma>([IDIOMA_BASE]);

const cargarCatalogo = async (idioma: Idioma): Promise<void> => {
  if (cargados.has(idioma)) return;
  const modulo = await import('./locales/en.json');
  i18n.addResourceBundle(idioma, 'translation', modulo.default ?? modulo, true, true);
  cargados.add(idioma);
};

/** Carga el catálogo del idioma, lo pone y lo recuerda. */
export const aplicarIdioma = async (idioma: Idioma): Promise<void> => {
  const destino = esIdioma(idioma) ? idioma : IDIOMA_BASE;
  await cargarCatalogo(destino);
  if (i18n.language !== destino) await i18n.changeLanguage(destino);
  recordar(destino);
  if (typeof document !== 'undefined') document.documentElement.lang = destino;
};

/** Antes de pintar la aplicación: el catálogo del idioma recordado. Nunca lanza. */
export const prepararIdioma = async (): Promise<void> => {
  try {
    if (esPaginaDelCliente()) {
      // El del cliente para este enlace, sin tocar el idioma recordado del usuario del despacho.
      const idioma = idiomaPublicoRecordado();
      if (idioma === IDIOMA_BASE) return;
      await cargarCatalogo(idioma);
      await i18n.changeLanguage(idioma);
      if (typeof document !== 'undefined') document.documentElement.lang = idioma;
      return;
    }
    await aplicarIdioma(idiomaRecordado());
  } catch {
    /* sin catálogo: la aplicación sale en español */
  }
};

export const idiomaActual = (): Idioma => (esIdioma(i18n.language) ? i18n.language : IDIOMA_BASE);

/** Para lo que no es un componente (servicios, constantes, avisos). */
export const t = i18n.t.bind(i18n);

export { useTranslation, Trans } from 'react-i18next';
export default i18n;
